import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQueries, useMutation } from "@tanstack/react-query";
import { useState } from "react";
import { SiteShell } from "@/components/layout/SiteShell";
import { Button } from "@/components/ui/button";
import { salons } from "@/data/salons";
import { getSalonReviews, syncAllReviews } from "@/lib/reviews.functions";
import type { SyncSummary } from "@/lib/reviews.functions";

export const Route = createFileRoute("/admin")({
  head: () => ({
    meta: [
      { title: "Admin — Tailor's Barber" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: AdminPage,
});

function AdminPage() {
  const fetchReviews = useServerFn(getSalonReviews);
  const runSync = useServerFn(syncAllReviews);

  const counts = useQueries({
    queries: salons.map((s) => ({
      queryKey: ["admin-reviews", s.slug],
      queryFn: () => fetchReviews({ data: { salonSlug: s.slug, limit: 50, minRating: 1 } }),
    })),
  });

  const [password, setPassword] = useState("");
  const [results, setResults] = useState<{
    bokadirekt: SyncSummary[];
    google: SyncSummary[];
  } | null>(null);

  const sync = useMutation({
    mutationFn: () => runSync({ data: { password } }),
    onSuccess: (data) => {
      setResults({ bokadirekt: data.bokadirekt, google: data.google });
      counts.forEach((q) => q.refetch());
    },
  });

  return (
    <SiteShell>
      <div className="mx-auto max-w-4xl px-4 py-10">
        <h1 className="text-3xl font-semibold">Admin — Recensioner</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Intern verktygssida för att synka recensioner från Google och BokaDirekt.
        </p>

        <section className="mt-8">
          <h2 className="text-xl font-semibold">Nuvarande antal per salong</h2>
          <div className="mt-4 overflow-x-auto rounded-lg border border-border">
            <table className="w-full text-left text-sm">
              <thead className="bg-muted/50">
                <tr>
                  <th className="px-4 py-2">Salong</th>
                  <th className="px-4 py-2">Antal recensioner (topp 50, alla betyg)</th>
                </tr>
              </thead>
              <tbody>
                {salons.map((s, i) => {
                  const q = counts[i];
                  return (
                    <tr key={s.slug} className="border-t border-border">
                      <td className="px-4 py-2">{s.shortName}</td>
                      <td className="px-4 py-2">
                        {q.isLoading ? "…" : q.isError ? "Fel" : (q.data?.length ?? 0)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>

        <section className="mt-8">
          <div className="flex flex-wrap items-center gap-3">
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Admin-lösenord"
              autoComplete="off"
              className="rounded-md border border-border bg-background px-3 py-2 text-sm"
            />
            <Button
              onClick={() => sync.mutate()}
              disabled={sync.isPending || !password}
            >
              {sync.isPending ? "Synkar…" : "Synka recensioner nu"}
            </Button>
          </div>
          {sync.isError && (
            <p className="mt-2 text-sm text-red-600">
              Fel: {(sync.error as Error).message}
            </p>
          )}
        </section>

        {results && (
          <section className="mt-8 space-y-6">
            <h2 className="text-xl font-semibold">Synk-resultat</h2>
            {(["bokadirekt", "google"] as const).map((src) => (
              <div key={src}>
                <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                  {src}
                </h3>
                <div className="mt-2 overflow-x-auto rounded-lg border border-border">
                  <table className="w-full text-left text-sm">
                    <thead className="bg-muted/50">
                      <tr>
                        <th className="px-4 py-2">Salong</th>
                        <th className="px-4 py-2">Scraped</th>
                        <th className="px-4 py-2">Inserted</th>
                        <th className="px-4 py-2">Fel</th>
                      </tr>
                    </thead>
                    <tbody>
                      {results[src].map((r) => (
                        <tr key={`${src}-${r.salon}`} className="border-t border-border">
                          <td className="px-4 py-2">{r.salon}</td>
                          <td className="px-4 py-2">{r.scraped}</td>
                          <td className="px-4 py-2">{r.inserted}</td>
                          <td className="px-4 py-2 text-red-600">{r.error ?? ""}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ))}
          </section>
        )}
      </div>
    </SiteShell>
  );
}
